import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import '../../lib/intersectionObserverPolyfill'; 

import { confService } from '../../Services';
import { usersService } from '../../Services';
import { itemsPerPage } from '../../Services'

import { setFilter } from '../../redux/stores/orgSlice';

import Modal from '../Common/Modal';

import {ReactComponent as Loading} from '../../images/icons/General/loading-01.svg';
import {ReactComponent as Plus} from '../../images/icons/General/plus.svg';
import {ReactComponent as Sunset} from '../../images/icons/Weather/sunset.svg';
import {ReactComponent as Sunrise} from '../../images/icons/Weather/sunrise.svg';
import {ReactComponent as Edit} from '../../images/icons/General/edit-01.svg';
import {ReactComponent as Waves} from '../../images/icons/Weather/waves.svg';
import {ReactComponent as Hand} from '../../images/icons/General/heart-hand.svg';
import {ReactComponent as Certificate} from '../../images/icons/Education/certificate-02.svg';
import {ReactComponent as User} from '../../images/icons/Users/user-01.svg';
import {ReactComponent as Search} from '../../images/icons/General/search-sm.svg';
import {ReactComponent as Dots} from '../../images/icons/General/dots-horizontal.svg';
import {ReactComponent as X} from '../../images/icons/General/x.svg';

export const Users = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [info, setInfo] = useState([]);
    const [dives, setDives] = useState({});
    const [divePlans, setDivePlans] = useState({});
    const [number, setNumber] = useState(0);
    const [menu, setMenu] = useState(0);
    const [groups, setGroups] = useState({});
    const [teams, setTeams] = useState({});
    const [group, setGroup] = useState({
                value: 0,
                label: 'All groups'
            });
    const [team, setTeam] = useState({
                value: 0,
                label: 'All teams'
            });
    const [searchQuery, setSearchQuery] = useState('');
    const [statusLoading, setStatusLoading] = useState(0);
    const [sortField, setSortField] = useState('LastName');
    const [sortOrder, setSortOrder] = useState(true);
    const local = useSelector((state) => state.org.remember);
    const filter = useSelector((state) => state.org.filter.user ?? null);
    const userId = useSelector((state) => state[local ? 'localUser' : 'user'].id);
    const permissions = useSelector((state) => state[local ? 'localUser' : 'user'].permissions).split(',');
    const modules = useSelector((state) => state.org.modules);
    const [newUser, setNewUser] = useState(false);
    const [search, setSearch] = useState('');
    const [confirmDeactivation, setConfirmDeactivation] = useState(0);
    const dispatch = useDispatch();
    const [page, setPage] = useState(-1);
    const [hasMore, setHasMore] = useState(true);
    const loaderRef = useRef(null);
    const tableRef = useRef(null);
    const initialPageRef = useRef(Math.max(parseInt(searchParams.get('page')) || 1, 1) - 1);
    const firstLoadRef = useRef(true);
    const scrollToIndexRef = useRef(null);

    const customStyles = {
        container: (provided, state) => ({
            ...provided,
            flex: '1',
            height: '42px'
          }),
        control: (provided, state) => ({
          ...provided,
          border: '1px solid #D0D5DD',
          minHeight: '44px',
          '&:hover': {
            boxShadow: '0 0 1px #5D3FC5'
          }
        })
    }

    const changeFilter = (value) => {
        dispatch(setFilter({
            name: 'user',
            value
        }));
        resetValues();
    }

    const resetValues = () => {
        setInfo([]);
        setDives({});
        setDivePlans({});
        setPage(-1);
        setHasMore(true);
        setNumber(0);
    }

    const sort = (e) => {
        if (e.target.id === sortField)
            setSortOrder(!sortOrder)
        else 
            setSortField(e.target.id);

        resetValues();
    }

    const setStatus = async (id, statusValue, confirm = false) => {
        if (id === userId && !statusValue && !confirm) {
            setConfirmDeactivation(id);
        } else {
            setStatusLoading(id);
            const res = await confService.userStatus({
                id: id,
                status: statusValue
            });

            if (res.result === 'OK') {
                setInfo((prev) =>
                    prev.map((u) =>
                        u.id === id ? { ...u, Status: statusValue } : u
                    )
                );

                setStatusLoading(0);
                setConfirmDeactivation(false);
            }
            
        }
    }

    const decline = async (id) => {
        setStatusLoading(id);
        const res = await confService.userStatus({
            id: id,
            status: -1
        });

        if (res.result === 'OK')
            setInfo((prev) =>
                prev.filter(item => item.id !== id)
            );

        setStatusLoading(0);
    }

    function resetMenu(event) {
        if (!event.target.classList.contains('dots') && event.target.tagName !== 'path')
            setMenu(0)
    }

    const fetchData = useCallback(async (pageNum, pagesCount, status, searchValue, sortFieldValue, sortOrderValue, groupValue, teamValue) => {
        const res = await confService.getUsers(pageNum, status, searchValue, sortFieldValue, sortOrderValue, groupValue ? groupValue.value : 0, teamValue ? teamValue.value : 0, pagesCount);
        setInfo(prev =>!pageNum ? res.list : [...prev, ...res.list ]);

        if (pagesCount > 1)
            scrollToIndexRef.current = itemsPerPage * (pagesCount - 1);

        setDives(prev => Object.assign({}, prev, res.dives));

        setDivePlans(prev => Object.assign({}, prev, res.divePlans));

        if (!pageNum) {
            setGroups([{
                value: 0,
                label: 'All groups'
            }, ...res.groups]);

            setTeams([{
                value: 0,
                label: 'All teams'
            }, ...res.teams]);
        }

        if (res.number)
            setNumber(res.number);

        if (res.list.length < itemsPerPage * pagesCount)
            setHasMore(false);
      }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
        (entries) => {
            if (entries[0].isIntersecting && hasMore) {
                setPage((prev) => prev === -1 && firstLoadRef.current ? initialPageRef.current : prev + 1);
            }
        },
        { threshold: 0.1 }
        );

        const loader = loaderRef.current;
        if (loader) observer.observe(loader);

        return () => {
            if (loader) observer.unobserve(loader);
        };
    }, [hasMore]);

    useEffect(() => {
        if (page < 0) return;
        // on the first load fetch all pages up to the one from the url in one request
        const pagesCount = firstLoadRef.current ? page + 1 : 1;
        firstLoadRef.current = false;
        fetchData(page + 1 - pagesCount, pagesCount, filter, searchQuery, sortField, sortOrder, group, team);
    }, [fetchData, page, filter, searchQuery, sortField, sortOrder, group, team]);

    useEffect(() => {
        if (page < 0) return;
        const urlPage = page > 0 ? String(page + 1) : null;
        if (searchParams.get('page') === urlPage) return;
        const newParams = new URLSearchParams(searchParams);
        if (urlPage)
            newParams.set('page', urlPage);
        else
            newParams.delete('page');
        setSearchParams(newParams, { replace: true });
    }, [page, searchParams, setSearchParams]);

    useEffect(() => {
        const index = scrollToIndexRef.current;
        if (index === null) return;
        scrollToIndexRef.current = null;
        const row = tableRef.current?.tBodies[0]?.rows[index];
        if (row)
            row.scrollIntoView({ block: 'start' });
    }, [info]);

    useEffect(() => {
        const handler = setTimeout(() => {
            if ((search.length >= 3 || !search) && search !== searchQuery) {
                setSearchQuery(search);
                resetValues();
            }
        }, 1000);

        return () => clearTimeout(handler);
    }, [search, searchQuery]);

    useEffect(() => {
        resetValues();
    }, [filter, searchQuery, sortField, sortOrder, group, team]);

    useEffect(() => {
        resetValues();
    }, [group]);

    return (
        <>
            <h1>Users</h1>
            {info ? <>
                <div className="bordered-content">
                    <div className="header">
                        <h4>{number} user{number === 1 ? '' : 's'}</h4>
                        <div className="buttons">
                            {groups.length > 1 && (
                                <Select className={`select`} options={groups} onChange={setGroup} styles={customStyles} required value={group} />
                            )}
                            {(teams.length > 1 && modules.includes('Schedule')) && (
                                <Select className={`select`} options={teams} onChange={setTeam} styles={customStyles} required value={team} />
                            )}
                            <div className="search">
                                <Search />
                                <input type="text" name="search" onKeyUp={(e) => setSearch(e.target.value)} placeholder="Enter user name..." />
                            </div>
                                {permissions.indexOf('Add user') > -1 ? <button className="primary" type="button" onClick={() => setNewUser(true)}><Plus />Add user</button> : ''}
                            </div>
                    </div>
                    <div className="filter">
                        <div className="filter-buttons">
                            <button type="button" className={filter === null ? 'active' : ''} onClick={() =>changeFilter(null)}>View all</button>
                            <button type="button" className={filter === 1 ? 'active' : ''} onClick={() =>changeFilter(1)}>Active</button>
                            <button type="button" className={filter === 0 ? 'active' : ''} onClick={() =>changeFilter(0)}>Inactive</button>
                            <button type="button" className={filter === 2 ? 'active' : ''} onClick={() =>changeFilter(2)}>Not Approved</button>
                        </div>
                    </div>
                    <table ref={tableRef}>
                        <thead>
                            <tr>
                                <th style={{width: '23%'}} className={`sortable ${sortField === 'LastName' ? (sortOrder ? 'down' : 'up') : ''}`} id="LastName" onClick={sort}>Last name</th>
                                <th style={{width: '23%'}} className={`sortable ${sortField === 'FirstName' ? (sortOrder ? 'down' : 'up') : ''}`} id="FirstName" onClick={sort}>First name</th>
                                <th style={{width: '23%'}} className={`sortable ${sortField === 'Email' ? (sortOrder ? 'down' : 'up') : ''}`} id="Email" onClick={sort}>Email</th>
                                <th style={{width: '7%'}} className="table-icon light">Dive logs</th>
                                <th style={{width: '7%'}} className="table-icon light">Dive plans</th>
                                <th style={{width: '7%'}} className="table-icon light">Status</th>  
                                <th style={{width: '6%'}} className="table-icon light">Edit</th>  
                                <th style={{width: '7%'}} className="table-icon light"></th>  
                            </tr>
                        </thead>
                        <tbody>
                            {info.length ? info.map(item => {
                                return (
                                    <tr key={item.id} className={!item.Status ? 'disabled' : (item.Status === 2 ? 'not-approved' : '')} onClick={resetMenu}>
                                        <td className={item.Status === 2 ? 'new' : ''}><strong className="mobile">User: </strong>{item.LastName}<span className="mobile">, {item.FirstName}</span></td>
                                        <td className="desktop">{item.FirstName}</td>
                                        <td><strong className="mobile">Email: </strong><a href={`mailto:${item.Email}`}>{item.Email}</a></td>
                                        <td className="table-icon desktop">{permissions.indexOf('View dive') > -1 ? <Link to={`/configuration/users/${item.id}/dives`}><label className="label link pointer" title="Dive history">{dives[item.id] ?? 0}</label></Link> : ''}</td>
                                        <td className="table-icon desktop"><label className="label">{divePlans[item.id] ?? 0}</label></td>
                                        <td className="table-icon desktop">{((item.Status === 2 && permissions.indexOf('Confirm user') > -1) || item.Status !== 2 ? 
                                                (statusLoading === item.id ? 
                                                    <Loading className="btn-loader" /> : 
                                                    item.Status === 1 ? 
                                                        <Sunset className="pointer" onClick={() => statusLoading === item.id ? {} : setStatus(item.id, item.Status === 1 ? 0 : 1)} title="Deactivate" /> :
                                                        <Sunrise className="pointer" onClick={() => statusLoading === item.id ? {} : setStatus(item.id, item.Status === 1 ? 0 : 1)} title="Activate" />
                                                ) : 
                                            '')}</td>
                                        <td className="table-icon desktop">{permissions.indexOf('Edit user') > -1 ? <Link to={`/configuration/users/${item.id}`}><Edit className="pointer" title="Edit user profile" /></Link> : ''}</td>
                                        <td className="table-icon desktop last">
                                            <Dots className="pointer dots" title="More" onClick={() => setMenu(item.id === menu ? 0 : item.id)} />
                                            {menu === item.id ? <ul className="dots-menu">
                                                {item.Status !== 2 ?
                                                <>
                                                    <li>{permissions.indexOf('Add dive') > -1 ? <Link to={`/configuration/users/${item.id}/dives/add`}><Waves className="pointer" />Log a dive</Link> : ''}</li>
                                                    <li><Link to={`/configuration/users/${item.id}/reciprocity`}><Hand className="pointer" />LOR (Letter of Reciprocity)</Link></li>
                                                    <li><Link to={`/configuration/users/${item.id}/training`}><Certificate className="pointer" />VOT (Verification of Training letter)</Link></li>
                                                </> : ''}
                                                {(item.Status === 2 && permissions.indexOf('Confirm user') > -1) ? <li><Link to="#" onClick={() => statusLoading === item.id ? {} : decline(item.id)}>{statusLoading === item.id ? <Loading className="btn-loader" /> : <X className="pointer" />} Decline registration</Link></li> : ''}
                                            </ul> : ''}
                                        </td>
                                        <td className="mobile">
                                            {permissions.indexOf('View dive') > -1 ? <Link to={`/configuration/users/${item.id}/dives`}><label className="label link">Dive logs({dives[item.id] ?? 0})</label></Link> : ''}
                                            <label className="label">Dive plans({divePlans[item.id] ?? 0})</label>
                                             {item.Status !== 2 ? 
                                                <>
                                                    {permissions.indexOf('Add dive') > -1 ? <Link to={`/configuration/users/${item.id}/dives/add`}><label className="label link">Log a dive</label></Link> : ''}
                                                    <Link to={`/configuration/users/${item.id}/reciprocity`}><label className="label link">Reciprocity</label></Link>
                                                    <Link to={`/configuration/users/${item.id}/training`}><label className="label link">Training</label></Link>
                                                </> : ''}
                                            {(item.Status === 2 && permissions.indexOf('Confirm user') > -1) || item.Status !== 2 ? <label className="label link" onClick={() => statusLoading === item.id ? {} : setStatus(item.id, item.Status === 1 ? 0 : 1)}>{statusLoading === item.id ? <Loading className="btn-loader" /> : ''}{item.Status === 1 ? 'Deactivate' : 'Activate'}</label> : ''}
                                            {(item.Status === 2 && permissions.indexOf('Confirm user') > -1) ? <Link to="#" onClick={() => statusLoading === item.id ? {} : decline(item.id)}><label className="label link">{statusLoading === item.id ? <Loading className="btn-loader" /> : ''} Decline registration</label></Link> : ''}
                                            {permissions.indexOf('Edit user') > -1 ? <Link to={`/configuration/users/${item.id}`}><label className="label link">Edit</label></Link> : ''}
                                        </td>
                                    </tr>
                                )}
                            ) : (hasMore ? <></> : <tr><td colSpan="9" className="load-more">No divers found</td></tr>)}
                            {hasMore && <tr ref={loaderRef}><td colSpan="9" className="load-more">Loading more...</td></tr>}
                        </tbody>
                    </table>
                </div>  
            </> : <p className="empty">User list is empty</p>}
            {newUser ? <ModalNewUser setClose={() => setNewUser(false)} /> : ''}
            {confirmDeactivation ? <ModalConfirmDeactivation setClose={() => setConfirmDeactivation(0)} setStatus={setStatus} user={confirmDeactivation} statusLoading={statusLoading} /> : ''}
        </>
    );
};

export default Users;

const ModalConfirmDeactivation = ({setClose, setStatus, user, statusLoading}) => {
    /*const tabs = [
        {
            title: 'New User',
            content: <NewUser updateInfo={updateInfo} />
        },
        {
            title: 'Existing user',
            content: <ExistingUser updateInfo={updateInfo} />
        }
    ];*/

    return (
        <Modal setClose={setClose} icon={<User />} type="wide">
            <h4>You are about to deactivate your own account. Are you sure?</h4>
            <div className="buttons">
                <button className="primary" type="button" disabled={statusLoading === user[0]} onClick={() => setStatus(user, 0, true)}>{statusLoading === user ? <Loading className="btn-loader" /> : 'Yes'}</button>
                <button className="white-bordered" type="button" onClick={() => setClose()} disabled={statusLoading === user}>Cancel</button>
            </div>
        </Modal>
    )
}

const ModalNewUser = ({setClose, updateInfo}) => {
    /*const tabs = [
        {
            title: 'New User',
            content: <NewUser updateInfo={updateInfo} />
        },
        {
            title: 'Existing user',
            content: <ExistingUser updateInfo={updateInfo} />
        }
    ];*/

    return (
        <Modal setClose={setClose} icon={<User />} type="wide">
            <h4>Add an AAUS user to this OM</h4>
            <ExistingUser updateInfo={updateInfo} />
        </Modal>
    )
}

const ExistingUser = () => {
    const [user, setUser] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const customStyles = {
        container: (provided, state) => ({
            ...provided,
            width: '100%',
          }),
        control: (provided, state) => ({
          ...provided,
          border: '1px solid #b0e2ff',
          'min-height': '44px'
        })
    }

    const loadOptions = async (inputValue) => {
        if (inputValue.length >= 2) {
            const data = await usersService.search({
                'name': inputValue
            });

            return data.list;
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        setError('');
        setLoading(true);

        let message = '';

        if (!user)
            message = 'Please, enter required fields';

        if (!message) {
            const res = await usersService.create({
                id: user.id,
                type: 'existing'
            });

            if (res.res === 'OK') {
                navigate('/configuration/users/' + user.id);
            } else {
                setError(res.error);
            }
        } else {
            setError(message);
        }
        setLoading(false);
    };

    return (
        <>
            <form onSubmit={handleSubmit} className={error ? 'required' : ''}>
                <div className="form-column">
                    <label>User</label>
                    <div>
                        <AsyncSelect
                            onChange={setUser}
                            styles={customStyles}
                            width='100%'
                            menuColor='red'
                            getOptionLabel={e => e.name+' ('+e.Email+')'}
                            getOptionValue={e => e.id}
                            loadOptions={loadOptions}
                            placeholder="Start typing a last name..."
                        />
                        <div className="font-14">You may search by the full last name and the full first name, separating them by comma. For example "Smith, John".</div>
                    </div>
                </div>
                <div className="form-submit">
                    <button type="submit" className="primary">{loading ? <Loading className="btn-loader" alt="loader" /> : ''}Add</button>
                    {error ? <div className="error">{error}</div> : ''}
                </div>
            </form>
        </>
    )
}